"use client";

import { Capacitor, registerPlugin } from "@capacitor/core";
import { useCallback, useRef, useState } from "react";
import { createClientLogger } from "@/lib/logging/client";
import {
  buildPermissionGuidance,
  DevicePermissionError,
  type DevicePermissionGuidance,
} from "@/lib/native/permission-guidance";
import { formatNativeStrengthTarget } from "@/lib/native/strength-target";
import {
  isNativeWorkoutEnded,
  markNativeWorkoutEnded,
  workoutNativeSessionId,
} from "@/lib/native/workout-identity";
import {
  type WeightUnit,
  weightFromKg,
} from "@/lib/weight-training/measurement-contract";
import { publishWatchActivity } from "./use-live-activity-commands";
import { waitForNativeSessionOwner } from "./use-native-session-owner";

const logger = createClientLogger("hooks/use-workout-live-activity");

export const WORKOUT_LIVE_ACTIVITY_MAX_AGE_MS = 8 * 60 * 60 * 1000;

type NativePlatform = "ios" | "android" | "web";
type WorkoutActivityState =
  | "workout"
  | "resting"
  | "rest-complete"
  | "workout-complete";

type HiiveWorkoutNotificationPlugin = {
  startTracking(input: {
    accountId: string;
    sessionId: string;
    title: string;
    message: string;
    startedAtMs: number;
  }): Promise<void>;
  update(input: {
    accountId: string;
    sessionId: string;
    title: string;
    message: string;
    state: WorkoutActivityState;
    startedAtMs: number;
    restEndMs?: number;
    exerciseCount: number;
    setCount: number;
  }): Promise<void>;
  clear(input: { accountId: string; sessionId: string }): Promise<void>;
};

const HiiveWorkoutNotification = registerPlugin<HiiveWorkoutNotificationPlugin>(
  "HiiveWorkoutNotification",
);

// Check if running in native Capacitor environment and on which platform.
const getNativePlatform = (): NativePlatform => {
  if (typeof window === "undefined") return "web";
  if (!Capacitor.isNativePlatform()) return "web";

  const platform = Capacitor.getPlatform();
  if (platform === "ios" || platform === "android") return platform;
  return "web";
};

interface WorkoutActivityInfo {
  preferredWeightUnit?: WeightUnit;
  accountId: string;
  sessionId: string;
  workoutName: string;
  exerciseCount: number;
  setCount: number;
  totalSetCount: number;
  totalVolumeKg: number;
  currentExercise: string;
  currentSetId: string;
  currentSetNumber: number;
  currentSetWeightKg: number;
  currentSetReps: number;
  currentSetDurationSeconds: number;
  currentSetDistanceMeters: number;
  currentSetTrackMode: "reps" | "time" | "distance";
  startedAt: number; // Timestamp in ms
  restStartMs?: number;
  restEndMs?: number;
}

type WorkoutWatchStats = {
  workoutName?: string;
  restTimer?: {
    active: boolean;
    restEndTime: number | null;
    totalSeconds: number;
  };
  preferredWeightUnit?: WeightUnit;
  exerciseCount: number;
  completedSetCount: number;
  totalSetCount: number;
  totalVolumeKg?: number;
  currentExercise: string | null;
  currentSetId: string | null;
  currentSetNumber: number | null;
  currentSetWeightKg: number | null;
  currentSetReps: number | null;
  currentSetDurationSeconds: number | null;
  currentSetDistanceMeters: number | null;
  currentSetTrackMode: "reps" | "time" | "distance";
};

function buildWatchWorkoutState(
  info: WorkoutActivityInfo,
  input: {
    state: string;
    title: string;
    message: string;
    restStartMs?: number;
    restEndMs?: number;
  },
) {
  return {
    accountId: info.accountId,
    activity: "workout" as const,
    state: input.state,
    title: input.title,
    message: input.message,
    startedAtMs: info.startedAt,
    sessionId: info.sessionId,
    exerciseCount: info.exerciseCount,
    setCount: info.setCount,
    totalSetCount: info.totalSetCount,
    totalVolumeKg: info.totalVolumeKg,
    preferredWeightUnit: info.preferredWeightUnit ?? "kg",
    // Always sent, unlike `title`, which the rest states replace with "Rest".
    workoutName: info.workoutName,
    currentExercise: info.currentExercise,
    currentSetId: info.currentSetId,
    currentSetNumber: info.currentSetNumber,
    currentSetWeightKg: info.currentSetWeightKg,
    currentSetReps: info.currentSetReps,
    currentSetDurationSeconds: info.currentSetDurationSeconds,
    currentSetDistanceMeters: info.currentSetDistanceMeters,
    currentSetTrackMode: info.currentSetTrackMode,
    restStartMs: input.restStartMs ?? info.restStartMs,
    restEndMs: input.restEndMs ?? info.restEndMs,
  };
}

/**
 * The content state the Live Activity actually reads.
 *
 * The card renders `exercises`, `sets`, `volume`, `currentExercise` and
 * `setTarget`; start() used to send `exerciseCount`, `setCount` and
 * `workoutTime`, which no view has ever looked at, and nothing anywhere sent
 * `volume` — so the Volume metric on the lock screen was permanently "—".
 * Building the payload in one place is what stops the two halves drifting
 * apart again.
 */
function buildWorkoutContentState(
  info: WorkoutActivityInfo,
  extra: Record<string, string> = {},
): Record<string, string> {
  return {
    accountId: info.accountId,
    title: info.workoutName || "Workout",
    startMs: String(info.startedAt),
    sessionId: info.sessionId,
    currentSetId: info.currentSetId,
    exercises: String(info.exerciseCount),
    sets: String(info.setCount),
    totalSets: String(info.totalSetCount),
    volume: formatVolume(
      weightFromKg(info.totalVolumeKg, info.preferredWeightUnit ?? "kg") ?? 0,
    ),
    volumeUnit: info.preferredWeightUnit ?? "kg",
    currentExercise: info.currentExercise,
    setTarget: formatNativeStrengthTarget(info),
    deepLink: "hiive://weight-training/workout/new",
    ...extra,
  };
}

/** `4,260` → `4.3k`. Matches the watch's `volumeText` so the two surfaces never
 * round the same number differently. */
function formatVolume(totalVolumeKg: number): string {
  if (!Number.isFinite(totalVolumeKg) || totalVolumeKg <= 0) return "—";
  return totalVolumeKg >= 10_000
    ? `${(totalVolumeKg / 1000).toFixed(1)}k`
    : String(Math.round(totalVolumeKg));
}

function buildWorkoutSummary(info: WorkoutActivityInfo) {
  const elapsed = Math.floor((Date.now() - info.startedAt) / 1000);
  return `${formatTime(elapsed)} • ${info.exerciseCount} exercises • ${info.setCount} sets`;
}

async function ensureAndroidNotificationPermission(
  LocalNotifications: typeof import("@capacitor/local-notifications").LocalNotifications,
) {
  const current = await LocalNotifications.checkPermissions();
  if (current.display === "granted") return true;

  const requested = await LocalNotifications.requestPermissions();
  return requested.display === "granted";
}

async function startAndroidWorkoutNotification(info: WorkoutActivityInfo) {
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const hasPermission =
    await ensureAndroidNotificationPermission(LocalNotifications);
  if (!hasPermission) {
    throw new DevicePermissionError(
      "notifications",
      "Android notification permission is turned off for Hiive.",
    );
  }

  await waitForNativeSessionOwner(info.accountId);
  if (isNativeWorkoutEnded(info.sessionId)) return;
  await HiiveWorkoutNotification.startTracking({
    accountId: info.accountId,
    sessionId: info.sessionId,
    title: info.workoutName || "Workout in progress",
    message: buildWorkoutSummary(info),
    startedAtMs: info.startedAt,
  });
}

async function updateAndroidWorkoutNotification(
  info: WorkoutActivityInfo,
  body: string,
  state: WorkoutActivityState,
  restEndMs?: number,
) {
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const hasPermission =
    await ensureAndroidNotificationPermission(LocalNotifications);
  if (!hasPermission) {
    throw new DevicePermissionError(
      "notifications",
      "Android notification permission is turned off for Hiive.",
    );
  }

  await waitForNativeSessionOwner(info.accountId);
  if (isNativeWorkoutEnded(info.sessionId)) return;
  await HiiveWorkoutNotification.update({
    accountId: info.accountId,
    sessionId: info.sessionId,
    title: info.workoutName || "Workout in progress",
    message: body,
    state,
    startedAtMs: info.startedAt,
    restEndMs,
    exerciseCount: info.exerciseCount,
    setCount: info.setCount,
  });
}

async function endAndroidWorkoutNotification(info: WorkoutActivityInfo) {
  await HiiveWorkoutNotification.clear({
    accountId: info.accountId,
    sessionId: info.sessionId,
  });
}

const iosStarts = new Map<string, Promise<void>>();
async function ensureIosWorkoutActivity(info: WorkoutActivityInfo) {
  const pending = iosStarts.get(info.sessionId);
  if (pending) return pending;
  const task = (async () => {
    await waitForNativeSessionOwner(info.accountId);
    const { LiveActivity } = await import("capacitor-live-activity");
    const running = await LiveActivity.isRunning({ id: info.sessionId });
    await waitForNativeSessionOwner(info.accountId);
    if (running.value || isNativeWorkoutEnded(info.sessionId)) return;
    await LiveActivity.startActivity({
      id: info.sessionId,
      attributes: { type: "workout", accountId: info.accountId },
      contentState: buildWorkoutContentState(info, {
        state: "workout",
        message: "Workout in progress",
      }),
      timestamp: Date.now(),
    });
    try {
      await waitForNativeSessionOwner(info.accountId);
    } catch (error) {
      // Account removal may have finished while ActivityKit was starting.
      await LiveActivity.endActivity({
        id: info.sessionId,
        contentState: {},
        dismissalPolicy: "immediate",
      });
      throw error;
    }
  })().finally(() => {
    iosStarts.delete(info.sessionId);
  });
  iosStarts.set(info.sessionId, task);
  return task;
}

/**
 * Uber-style unified Live Activity hook for workout sessions.
 *
 * States:
 * - "workout" - Normal workout mode (purple, count-up timer)
 * - "resting" - Rest period (orange, countdown timer)
 * - "rest-complete" - Brief celebration after rest
 * - "workout-complete" - Final celebration
 */
export function useWorkoutLiveActivity() {
  const [isActive, setIsActive] = useState(false);
  const [permissionGuidance, setPermissionGuidance] =
    useState<DevicePermissionGuidance | null>(null);
  const activityInfoRef = useRef<WorkoutActivityInfo | null>(null);
  const showPermissionGuidanceFromError = useCallback((error: unknown) => {
    if (!(error instanceof DevicePermissionError)) return false;

    setPermissionGuidance(
      buildPermissionGuidance(error.kind, {
        issue: error.issue,
        rawMessage: error.message,
      }),
    );
    return true;
  }, []);

  // Start workout Live Activity
  // startedAtMs: optional start time from session (for resuming with correct elapsed time)
  const startWorkoutActivity = useCallback(
    async (
      workoutName: string,
      startedAtMs: number,
      owner: { accountId: string; workoutId: string },
    ) => {
      const platform = getNativePlatform();
      if (
        platform === "web" ||
        isNativeWorkoutEnded(
          workoutNativeSessionId(owner.accountId, owner.workoutId),
        )
      )
        return;

      const startedAt = startedAtMs || Date.now();
      activityInfoRef.current = {
        accountId: owner.accountId,
        sessionId: workoutNativeSessionId(owner.accountId, owner.workoutId),
        workoutName,
        exerciseCount: 0,
        setCount: 0,
        totalSetCount: 0,
        totalVolumeKg: 0,
        currentExercise: "",
        currentSetId: "",
        currentSetNumber: 0,
        currentSetWeightKg: 0,
        currentSetReps: 0,
        currentSetDurationSeconds: 0,
        currentSetDistanceMeters: 0,
        currentSetTrackMode: "reps" as const,
        startedAt,
      };

      const info = activityInfoRef.current;
      setIsActive(true);
      await publishWatchActivity(
        buildWatchWorkoutState(info, {
          state: "workout",
          title: workoutName || "Workout",
          message: "Workout in progress",
        }),
      );

      try {
        if (platform === "android") {
          await startAndroidWorkoutNotification(info);
          await updateAndroidWorkoutNotification(
            info,
            buildWorkoutSummary(info),
            "workout",
          );
          setIsActive(true);
          logger.debug("[Workout] Android ongoing workout notification posted");
          return;
        }

        const { LiveActivity } = await import("capacitor-live-activity");

        // Check availability
        const available = await LiveActivity.isAvailable();
        if (!available.value) {
          logger.debug("[Workout] Live Activity not available");
          return;
        }

        await ensureIosWorkoutActivity(info);

        setIsActive(true);
        logger.debug(
          "[Workout] Live Activity started with startedAt:",
          startedAt,
        );
      } catch (error) {
        if (showPermissionGuidanceFromError(error)) {
          return;
        }
        logger.warn("[Workout] Failed to start Live Activity:", error);
      }
    },
    [showPermissionGuidanceFromError],
  );

  // Update exercise/set count (stays in workout state)
  const updateWorkoutStats = useCallback(
    async (stats: WorkoutWatchStats) => {
      const info = activityInfoRef.current;
      if (!isActive || !info) return;

      if (stats.workoutName !== undefined) info.workoutName = stats.workoutName;
      if (stats.restTimer) {
        const deadline = stats.restTimer.active
          ? stats.restTimer.restEndTime
          : null;
        info.restEndMs = deadline ?? 0;
        info.restStartMs = deadline
          ? deadline - stats.restTimer.totalSeconds * 1000
          : 0;
      }
      info.exerciseCount = stats.exerciseCount;
      info.setCount = stats.completedSetCount;
      info.totalSetCount = stats.totalSetCount;
      info.totalVolumeKg = stats.totalVolumeKg ?? 0;
      info.preferredWeightUnit = stats.preferredWeightUnit ?? "kg";
      info.currentExercise = stats.currentExercise ?? "";
      info.currentSetId = stats.currentSetId ?? "";
      info.currentSetNumber = stats.currentSetNumber ?? 0;
      info.currentSetWeightKg = stats.currentSetWeightKg ?? 0;
      info.currentSetReps = stats.currentSetReps ?? 0;
      info.currentSetDurationSeconds = stats.currentSetDurationSeconds ?? 0;
      info.currentSetDistanceMeters = stats.currentSetDistanceMeters ?? 0;
      info.currentSetTrackMode = stats.currentSetTrackMode;

      const resting = (info.restEndMs ?? 0) > Date.now();
      await publishWatchActivity(
        buildWatchWorkoutState(info, {
          state: resting ? "resting" : "workout",
          title: info.workoutName || "Workout",
          message: stats.currentExercise
            ? `Next: ${stats.currentExercise}`
            : stats.completedSetCount > 0
              ? `${stats.completedSetCount} sets completed`
              : `${stats.exerciseCount} exercises added`,
        }),
      );

      try {
        const platform = getNativePlatform();
        if (platform === "android") {
          await updateAndroidWorkoutNotification(
            info,
            buildWorkoutSummary(info),
            resting ? "resting" : "workout",
            info.restEndMs,
          );
          return;
        }
        if (platform !== "ios") return;

        const { LiveActivity } = await import("capacitor-live-activity");

        await LiveActivity.updateActivity({
          id: info.sessionId,
          contentState: buildWorkoutContentState(info, {
            state: resting ? "resting" : "workout",
            restStartMs: String(info.restStartMs ?? 0),
            restEndMs: String(info.restEndMs ?? 0),
            message:
              stats.completedSetCount > 0
                ? `${stats.completedSetCount} sets completed`
                : `${stats.exerciseCount} exercises added`,
          }),
        });
      } catch (error) {
        if (showPermissionGuidanceFromError(error)) return;
        // Silent fail on update
      }
    },
    [isActive, showPermissionGuidanceFromError],
  );

  // Switch to resting state with countdown
  const startRestPeriod = useCallback(
    async (restSeconds: number) => {
      const info = activityInfoRef.current;
      if (!isActive || !info) return;

      const restEndTime = Date.now() + restSeconds * 1000;
      info.restStartMs = Date.now();
      info.restEndMs = restEndTime;

      await publishWatchActivity(
        buildWatchWorkoutState(info, {
          state: "resting",
          title: "Rest",
          message: "Recover for the next set",
          restStartMs: Date.now(),
          restEndMs: restEndTime,
        }),
      );

      try {
        const platform = getNativePlatform();
        if (platform === "android") {
          await updateAndroidWorkoutNotification(
            info,
            `Rest ${formatTime(restSeconds)} • ${info.setCount} sets done`,
            "resting",
            restEndTime,
          );
          return;
        }
        if (platform !== "ios") return;

        const { LiveActivity } = await import("capacitor-live-activity");

        await LiveActivity.updateActivity({
          id: info.sessionId,
          contentState: buildWorkoutContentState(info, {
            state: "resting",
            title: "Rest",
            restStartMs: String(Date.now()),
            restEndMs: String(restEndTime),
            restFallback: formatTime(restSeconds),
            message: "Recover for the next set",
          }),
        });

        logger.debug("[Workout] Switched to resting state");
      } catch (error) {
        if (showPermissionGuidanceFromError(error)) return;
        logger.warn("[Workout] Failed to start rest period:", error);
      }
    },
    [isActive, showPermissionGuidanceFromError],
  );

  // Update rest period countdown (for add/subtract time)
  const updateRestPeriod = useCallback(
    async (newRestSeconds: number) => {
      const info = activityInfoRef.current;
      if (!isActive || !info) return;

      const newRestEndTime = Date.now() + newRestSeconds * 1000;
      info.restEndMs = newRestEndTime;

      await publishWatchActivity(
        buildWatchWorkoutState(info, {
          state: "resting",
          title: "Rest",
          message: `${formatTime(newRestSeconds)} remaining`,
          restStartMs: Date.now(),
          restEndMs: newRestEndTime,
        }),
      );

      try {
        const platform = getNativePlatform();
        if (platform === "android") {
          await updateAndroidWorkoutNotification(
            info,
            `Rest ${formatTime(newRestSeconds)} • ${info.setCount} sets done`,
            "resting",
            newRestEndTime,
          );
          return;
        }
        if (platform !== "ios") return;

        const { LiveActivity } = await import("capacitor-live-activity");

        await LiveActivity.updateActivity({
          id: info.sessionId,
          contentState: buildWorkoutContentState(info, {
            state: "resting",
            title: "Rest",
            restStartMs: String(Date.now()),
            restEndMs: String(newRestEndTime),
            restFallback: formatTime(newRestSeconds),
            message: "Recover for the next set",
          }),
        });

        logger.debug(
          "[Workout] Updated rest period to",
          newRestSeconds,
          "seconds",
        );
      } catch (error) {
        if (showPermissionGuidanceFromError(error)) return;
        logger.warn("[Workout] Failed to update rest period:", error);
      }
    },
    [isActive, showPermissionGuidanceFromError],
  );

  // Switch back to workout state (after rest complete)
  const endRestPeriod = useCallback(
    async (showCelebration: boolean = true) => {
      const info = activityInfoRef.current;
      if (!isActive || !info) return;

      info.restStartMs = 0;
      info.restEndMs = 0;
      const endingInfo = info;
      await publishWatchActivity(
        buildWatchWorkoutState(info, {
          state: "workout",
          title: info.workoutName || "Workout",
          message: showCelebration
            ? "Rest complete · back to work"
            : "Keep going",
        }),
      );

      try {
        const platform = getNativePlatform();
        if (platform === "android") {
          await updateAndroidWorkoutNotification(
            info,
            `${info.setCount} sets done • Keep going`,
            showCelebration ? "rest-complete" : "workout",
          );
          if (showCelebration) {
            setTimeout(async () => {
              if (
                activityInfoRef.current !== endingInfo ||
                (endingInfo.restEndMs ?? 0) > Date.now()
              )
                return;
              try {
                await updateAndroidWorkoutNotification(
                  info,
                  `${info.setCount} sets done • Keep going`,
                  "workout",
                );
              } catch {
                // Next explicit update will recover.
              }
            }, 2000);
          }
          return;
        }
        if (platform !== "ios") return;

        const { LiveActivity } = await import("capacitor-live-activity");

        if (showCelebration) {
          // Brief celebration state
          await LiveActivity.updateActivity({
            id: info.sessionId,
            contentState: buildWorkoutContentState(info, {
              state: "rest-complete",
              title: "Rest Complete",
              message: "Back to work",
            }),
            alert: {
              title: "Rest Complete",
              body: "Time to hit the next set!",
              sound: "default",
            },
          });

          // After 2 seconds, switch back to workout
          setTimeout(async () => {
            if (
              activityInfoRef.current !== endingInfo ||
              (endingInfo.restEndMs ?? 0) > Date.now()
            )
              return;
            try {
              await LiveActivity.updateActivity({
                id: info.sessionId,
                contentState: buildWorkoutContentState(info, {
                  state: "workout",
                  message: "Keep going",
                }),
              });
            } catch {
              // Silent fail
            }
          }, 2000);
        } else {
          // Just switch back to workout
          await LiveActivity.updateActivity({
            id: info.sessionId,
            contentState: buildWorkoutContentState(info, {
              state: "workout",
              message: "Keep going",
            }),
          });
        }

        logger.debug("[Workout] Ended rest period");
      } catch (error) {
        if (showPermissionGuidanceFromError(error)) return;
        logger.warn("[Workout] Failed to end rest period:", error);
      }
    },
    [isActive, showPermissionGuidanceFromError],
  );

  // End workout with celebration
  const endWorkoutActivity = useCallback(
    async (
      completed: boolean = true,
      stopTimeMs?: number,
      target?: {
        accountId: string;
        workoutId: string;
        startedAt: number;
        workoutName: string;
      },
    ) => {
      const platform = getNativePlatform();
      const info = target
        ? {
            ...target,
            sessionId: workoutNativeSessionId(
              target.accountId,
              target.workoutId,
            ),
            exerciseCount: 0,
            setCount: 0,
            totalSetCount: 0,
            totalVolumeKg: 0,
            currentExercise: "",
            currentSetId: "",
            currentSetNumber: 0,
            currentSetWeightKg: 0,
            currentSetReps: 0,
            currentSetDurationSeconds: 0,
            currentSetDistanceMeters: 0,
            currentSetTrackMode: "reps" as const,
          }
        : activityInfoRef.current;
      const endedAtMs = stopTimeMs ?? Date.now();
      // The terminal frame is independent of optional lock-screen presentation.
      // No delayed callback may end a subsequently started workout.
      if (activityInfoRef.current?.sessionId === info?.sessionId) {
        activityInfoRef.current = null;
        setIsActive(false);
      }
      if (!info) return;
      markNativeWorkoutEnded(info.sessionId);
      if (platform === "web") return;
      await publishWatchActivity({
        ...buildWatchWorkoutState(info, {
          state: "ended",
          title: info.workoutName,
          message: "",
        }),
        disposition: completed ? "completed" : "discarded",
        endedAtMs,
      });
      try {
        if (platform === "android") {
          await endAndroidWorkoutNotification(info);
        } else {
          await iosStarts.get(info.sessionId)?.catch(() => {});
          const { LiveActivity } = await import("capacitor-live-activity");
          await LiveActivity.endActivity({
            id: info.sessionId,
            contentState: {
              state: "ended",
              title: completed ? "Workout complete" : "Workout discarded",
            },
            dismissalPolicy: completed ? "after" : "immediate",
            dismissalDate: (endedAtMs + 5000) / 1000,
          });
        }
      } catch (error) {
        logger.warn("[Workout] Failed to end Live Activity:", error);
      }
    },
    [],
  );

  const clearPermissionGuidance = useCallback(() => {
    setPermissionGuidance(null);
  }, []);

  return {
    isActive,
    permissionGuidance,
    clearPermissionGuidance,
    startWorkoutActivity,
    updateWorkoutStats,
    startRestPeriod,
    updateRestPeriod,
    endRestPeriod,
    endWorkoutActivity,
  };
}

// Helper to format time
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
}
